import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Users, RefreshCw, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { adminCandidates, downloadCandidatesCsv, apiError } from "@/lib/adminApi";
import { PROVINCE_COLORS } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";

const fmt = (iso) => new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });

export function CandidatesPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [withEmail, setWithEmail] = useState(false);

  const load = useCallback(() => adminCandidates(withEmail).then(setData).catch((e) => toast.error(apiError(e, "Could not load candidates"))), [withEmail]);
  useEffect(() => { load(); }, [load]);

  const csv = async () => {
    setBusy(true);
    try { await downloadCandidatesCsv(withEmail); toast.success("CSV downloaded"); } catch (e) { toast.error(apiError(e, "Export failed")); } finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-candidates">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0F172A] text-white"><Users className="h-5 w-5" /></span>
          <div>
            <p className="font-heading text-base font-semibold text-slate-900">Candidate assessments</p>
            <p className="text-xs text-slate-500" data-testid="admin-candidates-total">{data.total} total · {data.with_email} left an email · {data.by_kind.full} full checks · {data.by_kind.stream} stream calculations shown (latest 500). Anonymous unless the candidate volunteered an email.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" checked={withEmail} onChange={(e) => setWithEmail(e.target.checked)} data-testid="admin-candidates-with-email" className="h-4 w-4 accent-[#C8102E]" />
            <MailCheck className="h-4 w-4" /> With email only
          </label>
          <button onClick={load} data-testid="admin-candidates-refresh" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><RefreshCw className="h-4 w-4" /> Refresh</button>
          <button onClick={csv} disabled={busy} data-testid="admin-candidates-csv" className="inline-flex items-center gap-1.5 rounded-md bg-[#C8102E] px-3 py-2 text-sm font-semibold text-white hover:bg-[#A60C24] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
          </button>
        </div>
      </div>
      {data.rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="admin-candidates-empty">No assessments recorded yet. Every completed check or stream calculation will appear here.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>{["When", "Email", "Type", "Stream / outcome", "CRS", "CLB", "Age", "Education", "Experience", "Occupation", "Status in Canada", "Job offer", "Connections"].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((r, i) => <CandidateRow key={r.created_at + i} r={r} i={i} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResultCell({ r }) {
  if (r.kind !== "stream") {
    return <span className="text-slate-700">{r.summary?.eligible ?? 0} eligible · {r.summary?.potentially_eligible ?? 0} potential{r.eligible_streams?.length ? ` · ${r.eligible_streams.join(", ")}` : ""}</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <span className="rounded px-1.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: PROVINCE_COLORS[r.province] }}>{r.province}</span>
      <span className="text-slate-800">{r.stream_name}</span>
      <span className="font-mono-tech font-semibold">{r.score ?? "—"}</span>
      <StatusBadge status={r.status} />
    </div>
  );
}

const fmtEdu = (r) => `${String(r.education || "").replace("_", " ")}${r.eduInCanada ? ` (CA${r.eduProvince ? `-${r.eduProvince}` : ""})` : ""}`;

function CandidateRow({ r, i }) {
  return (
    <tr data-testid={`admin-candidate-row-${i}`}>
      <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{fmt(r.created_at)}</td>
      <td className="px-3 py-2.5 font-medium text-slate-800" data-testid={`admin-candidate-email-${i}`}>{r.email ? <a href={`mailto:${r.email}`} className="text-[#C8102E] hover:underline">{r.email}</a> : <span className="text-slate-300">—</span>}</td>
      <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.kind === "stream" ? "bg-[#FEF2F2] text-[#C8102E]" : "bg-slate-100 text-slate-700"}`}>{r.kind === "stream" ? "Stream" : "Full check"}</span></td>
      <td className="px-3 py-2.5"><ResultCell r={r} /></td>
      <td className="px-3 py-2.5 font-mono-tech">{r.crs}</td>
      <td className="px-3 py-2.5 font-mono-tech">{r.clb_min}{r.frenchCLB ? ` / FR ${r.frenchCLB}` : ""}</td>
      <td className="px-3 py-2.5">{r.age}</td>
      <td className="px-3 py-2.5 text-slate-700">{fmtEdu(r)}</td>
      <td className="px-3 py-2.5 text-slate-700">{r.foreignExp}y abroad · {r.canadianExp}y CA</td>
      <td className="px-3 py-2.5 text-slate-700">{r.occupation || "—"}{r.nocCode ? ` (${r.nocCode})` : ""} · TEER {r.noc_teer}</td>
      <td className="px-3 py-2.5 text-slate-700">{String(r.legalStatus).replace("_", " ")}</td>
      <td className="px-3 py-2.5 text-slate-700">{r.jobOffer ? `${r.jobOfferProvince} · TEER ${r.jobOfferTeer}` : "No"}</td>
      <td className="px-3 py-2.5 text-slate-700">{(r.connections || []).join(", ") || "—"}</td>
    </tr>
  );
}
